import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Select from 'react-select'
import { fetchAirports } from '../../../lib/queries'
import { flightSchema } from './schema'

function FlightModal({ isOpen, onClose, flightData, mode, onSubmit }) {
    const [airports, setAirports] = useState([])
    const [loading, setLoading] = useState(true)
    const { register, handleSubmit, reset, control, formState: { errors }, watch } = useForm({
        defaultValues: flightData || {},
        resolver: yupResolver(flightSchema),
        context: { isEdit: mode === 'update' }
    })

    // Watch for departure selection to filter arrival options
    const selectedDeparture = watch('departure')

    useEffect(() => {
        const loadAirports = async () => {
            try {
                const airportData = await fetchAirports()
                setAirports(airportData)
            } catch (error) {
                console.error("Error loading airports:", error)
            } finally {
                setLoading(false)
            }
        }

        if (isOpen) {
            loadAirports()
        }
    }, [isOpen])

    useEffect(() => {
        if (flightData) {
            const departureAirport = airports.find(a => a.code === flightData.departure)
            const arrivalAirport = airports.find(a => a.code === flightData.arrival)

            reset({
                ...flightData,
                departure: departureAirport ? {
                    value: departureAirport.code,
                    label: `${departureAirport.label} (${departureAirport.code})`
                } : null,
                arrival: arrivalAirport ? {
                    value: arrivalAirport.code,
                    label: `${arrivalAirport.label} (${arrivalAirport.code})`
                } : null
            })
        }
    }, [flightData, airports, reset])

    const airportOptions = airports.map(airport => ({
        value: airport.code,
        label: `${airport.label} (${airport.code})`
    }))

    if (!isOpen) return null

    return (
        <>
            <div className="modal-backdrop show"></div>
            <div className="modal d-block show" tabIndex="-1" role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {mode === 'update' ? 'Update Flight' : 'Add Flight'}
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {loading ? (
                                <div className="text-center">Loading airports...</div>
                            ) : (
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="mb-3">
                                        <label className="form-label">Flight Number:</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.flightNo ? 'is-invalid' : ''}`}
                                            {...register('flightNo')}
                                        />
                                        {errors.flightNo && (
                                            <div className="invalid-feedback">{errors.flightNo.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Departure Airport:</label>
                                        <Controller
                                            name="departure"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={airportOptions}
                                                    className={errors.departure ? 'is-invalid' : ''}
                                                    placeholder="Select departure airport"
                                                    isClearable
                                                />
                                            )}
                                        />
                                        {errors.departure && (
                                            <div className="invalid-feedback d-block">{errors.departure.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Arrival Airport:</label>
                                        <Controller
                                            name="arrival"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={airportOptions.filter(option =>
                                                        !selectedDeparture || option.value !== selectedDeparture.value
                                                    )}
                                                    className={errors.arrival ? 'is-invalid' : ''}
                                                    placeholder="Select arrival airport"
                                                    isClearable
                                                />
                                            )}
                                        />
                                        {errors.arrival && (
                                            <div className="invalid-feedback d-block">{errors.arrival.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Airline Logo URL:</label>
                                        <input
                                            type="url"
                                            className={`form-control ${errors.airlineLogo ? 'is-invalid' : ''}`}
                                            placeholder="https://example.com/airline-logo.png"
                                            {...register('airlineLogo')}
                                        />
                                        {errors.airlineLogo && (
                                            <div className="invalid-feedback">{errors.airlineLogo.message}</div>
                                        )}
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary">
                                            {mode === 'update' ? 'Update' : 'Add'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default FlightModal 